import { Box, Tooltip, useToast } from "@chakra-ui/react";
import { toPng } from "html-to-image";
import React, { useCallback, useRef } from "react";

import { IconButton } from "../Buttons";
import { CopyIcon } from "../Icons/CopyIcon";
import type { ChartSpec } from "./chart";
import { InsightsChartPart } from "./chart";

type CopyableChartProps = {
  data: ChartSpec;
  variant?: "default" | "insightsReport";
};

export const CopyableChart: React.FC<CopyableChartProps> = ({
  data,
  variant = "default",
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const toast = useToast();

  const handleCopy = useCallback(async () => {
    if (!chartRef.current) return;
    try {
      const dataUrl = await toPng(chartRef.current, {
        pixelRatio: 2,
        skipFonts: true,
      });
      const base64 = dataUrl.split(",")[1];
      const bin = atob(base64);
      const buf = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
      const blob = new Blob([buf], { type: "image/png" });
      await navigator.clipboard.write([
        new ClipboardItem({ "image/png": blob }),
      ]);
      toast({ title: "Chart copied", status: "success", duration: 2000 });
    } catch {
      toast({
        title: "Failed to copy chart",
        status: "error",
        duration: 2000,
      });
    }
  }, [toast]);

  return (
    <Box position="relative">
      <Tooltip label="Copy chart as image" data-copy-role="chart-button">
        <IconButton
          aria-label="Copy chart as image"
          icon={<CopyIcon fontSize="16px" />}
          variant="ghost"
          size="xs"
          position="absolute"
          top={1}
          right={1}
          zIndex={1}
          opacity={0.6}
          _hover={{ opacity: 1 }}
          onClick={handleCopy}
          data-copy-role="chart-button"
        />
      </Tooltip>
      <Box ref={chartRef} data-copy-role="chart">
        <InsightsChartPart data={data} variant={variant} />
      </Box>
    </Box>
  );
};
